import 'isomorphic-fetch';
import { parseUrl } from 'query-string';

import { httpStatus } from './httpStatuses';
import { logger } from '../../utils/logger';
import { randInt, chooseOne } from '../../utils/randomisation';

import { getRequiredContext } from '../../utils/mocking/requireContext';


export async function mockFetch(
  url,
  opts = {},
  fileLoader = mockFileLoader) {
  return createMockFetch(createMockResponse, fileLoader)(url, opts);
}

export function createMockResponse(data, url, opts = {}, responseBodyConverter = JSON.stringify) {
  const bodyConverter = responseBodyConverter || ((body) => body);
  const defaultOptions = {
    status: httpStatus.OK,
    statusText: httpStatus.getText(httpStatus.OK)
  };

  const options = {
    ...defaultOptions,
    ...opts
  };
  const response = new Response(bodyConverter(data), options);
  Object.defineProperty(response, 'url', { value: url, writable: false });
  return response;
}

export function createMockFetch(
  responseFactory = createMockResponse,
  fileLoader = mockFileLoader) {

  if (typeof responseFactory !== 'function') {
    throw Error(`responseFactory must be a function, ${typeof responseFactory} given`);
  }

  return async function fetch(url, options = {}) {
    const defaultOptions = {
      status: httpStatus.OK,
      minDelay: 2000,
      maxDelay: 5000,
      failResponses: [
        {
          status: httpStatus.InternalError,
          statusText: httpStatus.getText(httpStatus.InternalError),
          body: { error: 'Internal  server error!' }
        },
        {
          status: httpStatus.Forbidden,
          statusText: httpStatus.getText(httpStatus.Forbidden),
          body: { error: 'Full authentication is required!' }
        },
        {
          status: httpStatus.NotFound,
          statusText: httpStatus.getText(httpStatus.NotFound),
          body: { error: 'Resource not found!' }
        },
        {
          status: httpStatus.BadRequest,
          statusText: httpStatus.BadRequest,
          body: { error: 'Bad request!' }
        }
      ],
      failRate: 0,
      networkFailRate: 0
    };
    const finalOptions = {
      ...defaultOptions,
      ...options
    };

    try {
      const dataLocation = parseUrl(url);
      const resourceUrl = parseResourceUrl(dataLocation);

      const mockMetadata = await fileLoader(resourceUrl, dataLocation.query, finalOptions);
      const { minDelay, maxDelay, failRate, networkFailRate, status, statusText, failResponses } = mockMetadata.options;
      const delay = randInt(minDelay, maxDelay);
      return new Promise(
        (resolve, reject) => {
          setTimeout(() => {
            if (isFail(networkFailRate)) {
              // eslint-disable-next-line prefer-promise-reject-errors
              return reject('Network error');
            } if (isFail(failRate)) {
              const { body, ...opts } = chooseOne(failResponses);
              return resolve(responseFactory(body, resourceUrl, opts));
            }
            return resolve(responseFactory(mockMetadata.data, resourceUrl, { status, statusText }));
          }, delay);
        }
      );
    } catch (e) {
      logger.error(e);
      return Promise.reject(e);
    }
  };
}

export async function mockFileDataParser(fileData, params = {}, options = {}) {
  if (fileData) {
    let parsedData = null;
    if (typeof fileData.onFetching === 'function') {
      if (fileData.content) {
        throw new Error('Cannot define both "content" and "onFetching" function at the same time.');
      }

      parsedData = fileData.onFetching(params, options);
      if (parsedData instanceof Promise) {
        parsedData = await parsedData;
      }
    } else if (typeof fileData.content === 'object') {
      parsedData = fileData.content;
    } else if (typeof fileData === 'object') {
      parsedData = fileData;
    }

    if (typeof fileData.onFetched === 'function') {
      let statusCode = fileData.onFetched(parsedData, params);
      if (statusCode instanceof Promise) {
        statusCode = await statusCode;
      }

      return createMockMetadata(parsedData, statusCode);
    } if (parsedData) {
      const statusCode = fileData.options && fileData.options.status
        ? fileData.options.status
        : httpStatus.OK;
      return createMockMetadata(parsedData, statusCode);
    }

    if (typeof parsedData !== 'object' || parsedData === null) {
      const type = parsedData === null ? 'null' : typeof parsedData;
      throw new Error(`onFetching must return object, ${type} given.`);
    }
  }

  throw new Error('File data must be object, null or undefined given.');
}

export async function mockFileLoader(
  path,
  params,
  options,
  fileDataParser = mockFileDataParser,
  requireContext = getRequiredContext()
) {
  if (!requireContext) {
    throw new Error('requireContext is invalid.');
  }
  const mockFile = requireContext(path).default;
  const mockFileConfig = mockFile.options || {};
  const finalOptions = { ...options, ...mockFileConfig };

  const finalFileDataParser = fileDataParser ||
    (() => Promise.resolve(createMockMetadata(mockFile)));

  const parsedData = await finalFileDataParser(mockFile, params, finalOptions);
  return {
    data: parsedData.data,
    options: {
      ...finalOptions,
      ...parsedData.options
    }
  };
}

export function createMockMetadata(
  data,
  status = httpStatus.OK
) {
  if (!httpStatus.has(status)) {
    throw new Error(
      `Parameter status expect one of this values [${httpStatus.codes.join(',')}], ${status} given.`
    );
  }

  return {
    data: data,
    options: {
      status: status,
      statusText: httpStatus.getText(status)
    }
  };
}

function isFail(failRate) {
  return Math.random() < failRate;
}


function parseResourceUrl(dataLocation) {
  let resourceUrl = dataLocation.url;

  const matchPattern = resourceUrl.match(/^(.+fetch-mock.js)(\/.+)$/);
  if (matchPattern) {
    resourceUrl = matchPattern[1];
  }

  if (resourceUrl.indexOf('.') !== 0) {
    resourceUrl = `.${resourceUrl}`;
  }

  return resourceUrl;
}
